import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { headerService } from '../services/headers';
import { API_CONFIG } from '../config/api';
import { 
  saveNotificationPreferences, 
  loadNotificationPreferences, 
  checkBackendNotificationStatus
} from '../utils/notificationUtils';

interface NotificationStateContextType {
  isActive: boolean;
  isLoading: boolean;
  refreshStatus: () => Promise<void>;
  setActive: (active: boolean) => void;
}

const NotificationStateContext = createContext<NotificationStateContextType | undefined>(undefined);

interface NotificationStateProviderProps {
  children: ReactNode;
}

export const NotificationStateProvider: React.FC<NotificationStateProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasCheckedInitialStatus, setHasCheckedInitialStatus] = useState(false);

  // Log del estado inicial
  console.log('🔔 NotificationStateProvider - Estado inicial:', { 
    isActive, 
    isLoading, 
    hasCheckedInitialStatus, 
    userId: user?.uid 
  });

  // Verificar estado inicial al montar
  useEffect(() => {
    console.log('🔔 NotificationStateProvider - useEffect ejecutándose:', { 
      userId: user?.uid, 
      hasCheckedInitialStatus,
      timestamp: new Date().toISOString()
    });
    
    if (user?.uid && !hasCheckedInitialStatus) {
      console.log('👤 Usuario disponible, verificando estado inicial...');
      checkInitialStatus();
      setHasCheckedInitialStatus(true);
    } else {
      console.log('🔔 NotificationStateProvider - Condiciones no cumplidas:', { 
        hasUser: !!user?.uid, 
        hasChecked: hasCheckedInitialStatus 
      });
    }
  }, [user?.uid, hasCheckedInitialStatus]);

  // Log cuando cambie el estado isActive
  useEffect(() => {
    console.log('🔔 NotificationStateProvider - Estado isActive cambiado a:', isActive);
    console.log('🔔 Timestamp del cambio:', new Date().toISOString());
  }, [isActive]);

  const checkInitialStatus = async () => {
    try {
      if (!user?.uid) {
        console.log('❌ checkInitialStatus - No hay usuario');
        return;
      }
      
      console.log('🔄 Verificando estado inicial de notificaciones...');
      setIsLoading(true);
      
      // Primero intentar cargar desde localStorage como fallback
      const cachedPrefs = user?.uid ? loadNotificationPreferences(user.uid) : null;
      console.log('📱 Preferencias del localStorage:', cachedPrefs);
      
      if (cachedPrefs) {
        console.log('📱 Estado desde localStorage:', cachedPrefs.notificationsEnabled);
        // Establecer estado inicial desde localStorage
        setIsActive(cachedPrefs.notificationsEnabled);
        console.log('✅ Estado inicial establecido desde localStorage a:', cachedPrefs.notificationsEnabled);
      } else {
        console.log('📱 No hay preferencias en localStorage');
      }
      
      // Luego verificar estado real del backend
      console.log('🔄 Verificando estado real del backend...');
      const hasToken = await checkBackendNotificationStatus(user?.uid, API_CONFIG.BASE_URL, headerService.getHeaders('authenticated'));
      console.log('✅ Estado real del backend:', hasToken);
      
      // IMPORTANTE: Siempre actualizar el estado visual con el valor del backend
      console.log('🔄 Actualizando estado visual a:', hasToken);
      setIsActive(hasToken);
      console.log('✅ Estado visual actualizado a:', hasToken);
      
      // Actualizar localStorage con el estado real del backend
      if (cachedPrefs?.notificationsEnabled !== hasToken) {
        console.log('🔄 Estado diferente, actualizando localStorage...');
        saveNotificationPreferences(hasToken, user?.uid);
        console.log('✅ localStorage actualizado con estado del backend');
      } else {
        console.log('✅ Estado sincronizado entre backend y localStorage');
      }
      
    } catch (error) {
      console.error('❌ Error verificando estado inicial:', error);
      // Si falla la API, usar preferencias del localStorage
      const cachedPrefs = user?.uid ? loadNotificationPreferences(user.uid) : null;
      if (cachedPrefs) {
        console.log('📱 Usando estado del localStorage como fallback:', cachedPrefs.notificationsEnabled);
        setIsActive(cachedPrefs.notificationsEnabled);
      } else {
        console.log('📱 No hay estado en localStorage, estableciendo como inactivo por defecto');
        setIsActive(false);
      }
    } finally {
      setIsLoading(false);
      console.log('✅ checkInitialStatus completado, isLoading: false');
    }
  };



  const refreshStatus = async () => {
    console.log('🔄 Refrescando estado de notificaciones manualmente...');
    try {
      if (!user?.uid) {
        console.log('❌ No hay usuario para refrescar estado');
        return;
      }
      
      setIsLoading(true);
      
      // Verificar estado real del backend
      const hasToken = await checkBackendNotificationStatus(user?.uid, API_CONFIG.BASE_URL, headerService.getHeaders('authenticated'));
      console.log('✅ Estado refrescado del backend:', hasToken);
      
      // Actualizar estado visual
      setIsActive(hasToken);
      
      // Actualizar localStorage
      saveNotificationPreferences(hasToken, user?.uid);
      
      console.log('✅ Estado refrescado exitosamente');
      
      // Verificar que el estado se actualizó correctamente
      console.log('🔍 Verificación final del estado:', { 
        isActive: hasToken, 
        backendStatus: hasToken,
        localStorageUpdated: true 
      });
      
    } catch (error) {
      console.error('❌ Error refrescando estado:', error);
      // En caso de error, intentar usar localStorage como fallback
      try {
        const cachedPrefs = user?.uid ? loadNotificationPreferences(user.uid) : null;
        if (cachedPrefs) {
          console.log('📱 Usando localStorage como fallback después del error:', cachedPrefs.notificationsEnabled);
          setIsActive(cachedPrefs.notificationsEnabled);
        }
      } catch (fallbackError) {
        console.error('❌ Error también en fallback del localStorage:', fallbackError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const setActive = (active: boolean) => {
    console.log('🔔 setActive llamado con:', active);
    console.log('🔔 Estado anterior en contexto:', isActive);
    
    setIsActive(active);
    
    console.log('🔔 setIsActive llamado, nuevo estado:', active);
    
    // También actualizar localStorage cuando se cambie el estado
    try {
      if (user?.uid) {
        saveNotificationPreferences(active, user.uid);
        console.log('✅ localStorage actualizado desde setActive');
      }
    } catch (error) {
      console.error('❌ Error actualizando localStorage desde setActive:', error);
    }
  };



  const value: NotificationStateContextType = {
    isActive,
    isLoading,
    refreshStatus,
    setActive
  };

  // Log del valor que se está proporcionando
  console.log('🔔 NotificationStateProvider - Valor del contexto:', { 
    isActive, 
    isLoading, 
    hasCheckedInitialStatus,
    userId: user?.uid,
    timestamp: new Date().toISOString()
  });

  return (
    <NotificationStateContext.Provider value={value}>
      {children}
    </NotificationStateContext.Provider>
  );
};

export const useNotificationStateContext = (): NotificationStateContextType => {
  const context = useContext(NotificationStateContext);
  
  if (context === undefined) {
    throw new Error('useNotificationStateContext must be used within a NotificationStateProvider');
  }
  
  return context;
};

export default NotificationStateProvider;
